import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BarChart3, PieChart, MapPin, Building, ExternalLink, UserCheck, Calendar, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();
  
  const effectiveRole = previewRole || user.role;

  useEffect(() => {
    fetchAdminStats();
  }, [startDate, endDate]);

  const fetchAdminStats = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const response = await axios.get(`${API}/admin/stats?${params.toString()}`);
      setStats(response.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const handlePriorityClick = (priority) => {
    navigate(`/tickets?priority=${priority}`);
  };

  const handleDealershipClick = (dealership) => {
    navigate(`/tickets?dealership=${encodeURIComponent(dealership)}`);
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-600 dark:text-white">Loading analytics...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white" data-testid="admin-dashboard-title">
              Analytics Dashboard
            </h1>
            <p className="text-slate-600 dark:text-white mt-1">Overview of ticket metrics and trends</p>
          </div>
        </div>

        {/* Date Filter */}
        <Card className="border-0 shadow-md mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-white">
                <Calendar className="w-4 h-4" />
                Filter by Date
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-500 dark:text-slate-400">From</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                    data-testid="analytics-start-date"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-500 dark:text-slate-400">To</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                    data-testid="analytics-end-date"
                  />
                </div>
                {(startDate || endDate) && (
                  <Button variant="ghost" size="sm" onClick={clearDateFilter} className="text-slate-500 hover:text-red-600" data-testid="analytics-clear-date">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
              {(startDate || endDate) && (
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  Showing filtered results ({stats?.total_tickets || 0} tickets)
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-white">Total Tickets</p>
                  <p className="text-4xl font-bold text-slate-800 dark:text-white mt-2" data-testid="admin-total-tickets">
                    {stats?.total_tickets || 0}
                  </p>
                </div>
                <div className="bg-blue-100 p-4 rounded-lg">
                  <BarChart3 className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-white">Total Users</p>
                  <p className="text-4xl font-bold text-slate-800 dark:text-white mt-2" data-testid="admin-total-users">
                    {stats?.total_users || 0}
                  </p>
                </div>
                <div className="bg-green-100 p-4 rounded-lg">
                  <PieChart className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {/* Status Distribution */}
          <Card className="border-0 shadow-md flex flex-col h-[500px]">
            <CardHeader className="border-b border-slate-100 flex-shrink-0">
              <CardTitle className="flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Tickets by Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 flex-1 overflow-hidden">
              {stats?.status_distribution && stats.status_distribution.length > 0 ? (
                <div className="space-y-4 h-full overflow-y-auto pr-2">
                  {stats.status_distribution.map((item) => (
                    <div key={item._id} className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-white capitalize w-32">
                          {item._id?.replace('_', ' ') || 'Unknown'}
                        </span>
                        <div className="flex-1 mx-4">
                          <div className="bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${(item.count / (stats.total_tickets || 1)) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-slate-800 dark:text-white w-12 text-right">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 dark:text-white py-8">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* Priority Distribution */}
          <Card className="border-0 shadow-md flex flex-col h-[500px]">
            <CardHeader className="border-b border-slate-100 flex-shrink-0">
              <CardTitle className="flex items-center">
                <PieChart className="w-5 h-5 mr-2 text-purple-600" />
                Tickets by Priority
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-white mt-1">Click to view those tickets</p>
            </CardHeader>
            <CardContent className="p-6 flex-1 overflow-hidden">
              {stats?.priority_distribution && stats.priority_distribution.length > 0 ? (
                <div className="space-y-4 h-full overflow-y-auto pr-2">
                  {stats.priority_distribution.map((item) => {
                    const colors = { low: 'bg-green-600', medium: 'bg-yellow-600', high: 'bg-orange-600', critical: 'bg-red-600' };
                    return (
                      <div 
                        key={item._id} 
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                        onClick={() => handlePriorityClick(item._id)}
                      >
                        <div className="flex items-center flex-1">
                          <span className="text-sm font-medium text-slate-700 dark:text-white capitalize w-32">
                            {item._id || 'Unknown'}
                          </span>
                          <div className="flex-1 mx-4">
                            <div className="bg-slate-200 rounded-full h-2">
                              <div
                                className={`${colors[item._id] || 'bg-gray-600'} h-2 rounded-full`}
                                style={{ width: `${(item.count / (stats.total_tickets || 1)) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800 dark:text-white">{item.count}</span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-slate-500 dark:text-white py-8">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* Tickets Assigned To (Technicians) */}
          <Card className="border-0 shadow-md flex flex-col h-[500px]">
            <CardHeader className="border-b border-slate-100 flex-shrink-0">
              <CardTitle className="flex items-center">
                <UserCheck className="w-5 h-5 mr-2 text-emerald-600" />
                Tickets Assigned To
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-white mt-1">Click to view those tickets</p>
            </CardHeader>
            <CardContent className="p-6 flex-1 overflow-hidden">
              {stats?.assigned_distribution && stats.assigned_distribution.length > 0 ? (
                <div className="space-y-3 h-full overflow-y-auto pr-2">
                  {stats.assigned_distribution.map((item, index) => (
                    <div key={item._id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-800 dark:text-white truncate max-w-[160px]">
                          {index === 0 && "👑 "}{item._id || 'Unassigned'}
                        </span>
                        <span className="text-sm font-bold text-slate-800 dark:text-white">
                          {item.count}
                        </span>
                      </div>
                      <div className="bg-slate-200 dark:bg-slate-700 rounded-full h-2 mb-2">
                        <div
                          className="bg-emerald-600 h-2 rounded-full"
                          style={{ width: `${(item.count / (stats.assigned_distribution[0]?.count || 1)) * 100}%` }}
                        />
                      </div>
                      <div className="flex gap-4 text-xs">
                        <span className="text-green-600 dark:text-green-400">
                          Closed: {item.closed || 0}
                        </span>
                        <span className="text-blue-600 dark:text-blue-400">
                          Open: {item.open || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 dark:text-white py-8">No assigned tickets</p>
              )}
            </CardContent>
          </Card>

          {/* Dealership Distribution */}
          <Card className="border-0 shadow-md flex flex-col h-[500px]">
            <CardHeader className="border-b border-slate-100 flex-shrink-0">
              <CardTitle className="flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-red-600" />
                Tickets by Dealership
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-white mt-1">Click to view those tickets</p>
            </CardHeader>
            <CardContent className="p-6 flex-1 overflow-hidden">
              {stats?.location_distribution && stats.location_distribution.length > 0 ? (
                <div className="space-y-4 h-full overflow-y-auto pr-2">
                  {stats.location_distribution.map((item) => (
                    <div 
                      key={item._id || 'unknown'} 
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                      onClick={() => handleDealershipClick(item._id)}
                    >
                      <div className="flex items-center flex-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-white w-40 truncate">
                          {item._id || 'Not Specified'}
                        </span>
                        <div className="flex-1 mx-4">
                          <div className="bg-slate-200 rounded-full h-2">
                            <div className="bg-red-600 h-2 rounded-full" style={{ width: `${(item.count / (stats.total_tickets || 1)) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800 dark:text-white">{item.count}</span>
                        <ExternalLink className="w-3 h-3 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 dark:text-white py-8">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* Department Distribution */}
          <Card className="border-0 shadow-md flex flex-col h-[500px]">
            <CardHeader className="border-b border-slate-100 flex-shrink-0">
              <CardTitle className="flex items-center">
                <Building className="w-5 h-5 mr-2 text-teal-600" />
                Tickets by Department
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 flex-1 overflow-hidden">
              {stats?.department_distribution && stats.department_distribution.length > 0 ? (
                <div className="space-y-4 h-full overflow-y-auto pr-2">
                  {stats.department_distribution.map((item) => (
                    <div key={item._id || 'unknown'} className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-white w-32">
                          {item._id || 'Not Specified'}
                        </span>
                        <div className="flex-1 mx-4">
                          <div className="bg-slate-200 rounded-full h-2">
                            <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${(item.count / (stats.total_tickets || 1)) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-slate-800 dark:text-white w-12 text-right">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 dark:text-white py-8">No data available</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
